/**
 * Solanaland
 *
 * Author: Phia Nasol
 * Company: Digiteer Inc.
 *
 * https://www.digiteer.digital/
 *
 */

import React from "react";
import { Accordion, Container, Row, Col, useAccordionButton } from "react-bootstrap";
import AccordionContext from "react-bootstrap/AccordionContext";
import { Button, Card, Select } from "core/components";
import { Controller, useFormContext } from "react-hook-form";
import { TaskFormData } from "core/schema";
import ChangeOrderContext from "./ProjectChangeOrderContext";
import ChangeOrderTasksContext from "./ChangeOrderTasksContext";
import ChangeOrderTaskItemsForm from "./ChangeOrderTaskItemsForm";

function CustomToggle({ eventKey }) {
  const accordionContext = React.useContext(AccordionContext);
  const decoratedOnClick = useAccordionButton(eventKey);

  const isOpen = accordionContext.activeEventKey?.includes(eventKey);
  const icon = isOpen ? "chevron-up" : "chevron-down";

  return <Button className={"button--default"} type="button" onClick={decoratedOnClick} icon={icon} iconProps={{ className: "svg-icon h5" }} />;
}

const ChangeOrderTaskForm = ({ changeOrder, index, remove, tasks, items, isItemsLoading }): JSX.Element => {
  const { setTasksForDestruction } = React.useContext(ChangeOrderContext);
  const { selectedTasks, setSelectedTasks } = React.useContext(ChangeOrderTasksContext);
  const {
    register,
    control,
    watch,
    resetField,
    formState: { errors }
  } = useFormContext();
  const fieldArrayName = "change_order_tasks_attributes";
  const field = `${fieldArrayName}.[${index}]`;
  const templateTask = watch(field);
  const [selectedTask, setSelectedTask] = React.useState<TaskFormData>(tasks.find((task) => task.id == templateTask?.task_id));

  const addToSelectedTasks = (task) => {
    if (selectedTasks.find((i) => i?.id == task?.id)) {
      return;
    } else {
      setSelectedTasks((value) => [...value, task]);
    }
  };

  const removeFromSelectedTasks = (task) => {
    if (selectedTasks.find((selectedTask) => selectedTask?.id == task?.id)) {
      setSelectedTasks(selectedTasks.filter((selectedTask) => selectedTask?.id != task.id) || []);
    }
  };

  React.useEffect(() => {
    if (selectedTask) {
      addToSelectedTasks(selectedTask);
    }
  }, [selectedTask]);

  return (
    <React.StrictMode>
      <Card className="card">
        <input hidden={true} {...register(`change_order_tasks_attributes.${index}.id` as const)} />
        <input hidden={true} {...register(`change_order_tasks_attributes.${index}.change_order_id` as const)} />
        <Card.Header
          title={selectedTask ? `${selectedTask?.task_classification?.name} | ${selectedTask.code} - ${selectedTask.name}` : "New Task"}
          titleProps={{ className: "h5" }}
        >
          {errors.change_order_tasks_attributes?.[index] && <Button className="button--danger mr-2" icon="exclamation-triangle" />}
          {/* <Button
            className="button--danger mr-2"
            icon="minus"
            iconProps={{ className: "svg-icon h5" }}
            onClick={() => {
              if (templateTask?.id) {
                setTasksForDestruction((value) => [...value, { ...templateTask, _destroy: "1" }]);
              }
              resetField(field, { defaultValue: {} });
              removeFromSelectedTasks(selectedTask);
              remove(index);
            }}
          /> */}
          <CustomToggle eventKey={index.toString()} />
        </Card.Header>
        <Accordion.Collapse eventKey={index.toString()}>
          <Card.Body>
            <Container fluid>
              <Row>
                <Col>
                  <Controller
                    name={`${fieldArrayName}.${index}.task_id`}
                    control={control}
                    render={({ field }) => (
                      <Select
                        label={`${changeOrder?.type == "replace" ? "Current Task" : "Task"}`}
                        placeholder="Select a Task"
                        required
                        noOptionsMessage={() => "No available task."}
                        options={tasks.filter(
                          (task) =>
                            (!selectedTasks.find((selected) => selected?.id == task?.id && field?.value != task?.id) && task.code != "CHG") || null
                        )}
                        getOptionLabel={(option) => `${option.code} - ${option.name}`}
                        getOptionValue={(option) => option.id}
                        disabled={true}
                        onChange={(option, actionMeta) => {
                          field.onChange(option?.id);

                          if (selectedTask) {
                            removeFromSelectedTasks(selectedTask);
                          }

                          setSelectedTask(option);

                          if (actionMeta.action == "select-option") {
                            addToSelectedTasks(option);
                          } else {
                            removeFromSelectedTasks(option || actionMeta?.removedValues[0]);
                          }
                        }}
                        ref={field.ref}
                        value={tasks.find((task) => task.id == field.value) || null}
                        errorMessage={errors.change_order_tasks_attributes?.[index]?.task_id?.message}
                      />
                    )}
                  />
                </Col>
                {/* {changeOrder.type == "replace" && (
                  <>
                    <Col>
                      <Controller
                        name={`${fieldArrayName}.${index}.replacement_task_id`}
                        control={control}
                        render={({ field }) => (
                          <Select
                            label="Replacement Task"
                            placeholder="Select a Replacement Task"
                            required
                            noOptionsMessage={() => "No available replacement task."}
                            options={tasks.filter(
                              (task) =>
                                (!selectedTasks.find((selected) => selected?.id == task?.id && field?.value != task?.id) &&
                                  task.code != "CHG" &&
                                  task.code != "EXS") ||
                                null
                            )}
                            formatOptionLabel={(option) => `${option.code} - ${option.name}`}
                            getOptionLabel={(option) => option.name}
                            getOptionValue={(option) => option.id}
                            isOptionDisabled={(option) => !option.active || selectedTask?.id == option.id}
                            onChange={(option) => {
                              field.onChange(option?.id);
                            }}
                            value={selectedTasks.find((selectedTask) => selectedTask?.id == field.value) || null}
                            errorMessage={errors.change_order_tasks_attributes?.[index]?.prerequisite_task_id?.message}
                          />
                        )}
                      />
                    </Col>
                  </>
                )} */}
              </Row>
              <ChangeOrderTaskItemsForm
                parentIndex={index}
                task={selectedTask}
                items={items}
                changeOrder={changeOrder}
                isItemsLoading={isItemsLoading}
              />
            </Container>
          </Card.Body>
        </Accordion.Collapse>
      </Card>
    </React.StrictMode>
  );
};

export default ChangeOrderTaskForm;
